import { useEffect, useRef } from "react";
import { useCompanyStore } from "store/company.store";
import useSWR, { mutate } from "swr";
import type { IUser } from "types/user.types";
import { identifyAnalyticsUser, resetAnalyticsIdentity } from "utils/analytics";
import {
	reportExtensionBuild,
	resetExtensionBuildReport,
} from "utils/extensionBuild";
import * as fb from "utils/firebase";
import { identifyPostHogUser, resetPostHogUser } from "utils/posthog";

export function useUser() {
	const { data, error, isLoading } = useSWR<IUser>("user", {
		revalidateOnFocus: false,
	});
	const refreshUser = () => mutate("user");
	const resetCashe = useCompanyStore((state) => state.reset);
	const identifiedIdRef = useRef<number | null>(null);

	useEffect(() => {
		if (data && data.id !== identifiedIdRef.current) {
			identifyPostHogUser(data);
			// Same id on the Segment/GA side, so an activation event can be joined
			// to the same person across sessions and devices. Traits stay thin on
			// purpose — see identifyAnalyticsUser.
			identifyAnalyticsUser(data.id, {
				accessLevel: data.accessLevel,
				admin: data.admin === true,
			});
			identifiedIdRef.current = data.id;
			// Which of the two published extensions this person is running.
			// Strictly after identify, or the event attaches to the anonymous
			// device and the person properties have no person to land on.
			// Deliberately not awaited: it probes two chrome-extension:// URLs
			// and must not hold up the app shell.
			void reportExtensionBuild(data.id);
		}
	}, [data]);

	const logout = async () => {
		resetPostHogUser();
		resetAnalyticsIdentity();
		identifiedIdRef.current = null;
		resetExtensionBuildReport();
		fb.logout();
		resetCashe();
		// Clear all SWR cache to prevent stale data from previous user
		await mutate(() => true, undefined, { revalidate: false });
	};

	// Auto-logout on a persisted 401 must run as an effect, not during render.
	// Calling logout() in the render body clears the SWR cache + resets the
	// company store synchronously, which re-renders this hook while the same
	// 401 error is still present, re-triggering logout() on every render and
	// crashing with "Maximum update depth exceeded".
	useEffect(() => {
		if (error?.config?._retry && error.response?.status === 401) {
			logout(); // Fire and forget - no need to await here
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error]);

	return {
		user: data,
		isLoading,
		error,
		logout,
		refreshUser,
	};
}
